import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import useModuleTracking from "./lib/useModuleTracking";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PlanProvider } from "./contexts/PlanContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LandingPagePremium from "./pages/LandingPagePremium";
import DemoRequestPublicPage from "./pages/DemoRequestPublicPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import DashboardPage from "./pages/DashboardPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import HRIndicatorsPage from "./pages/HRIndicatorsPage";
import ProspectsAdminPage from "./pages/ProspectsAdminPage";
import EmployeesPage from "./pages/EmployeesPage";
import EmployeeFormPage from "./pages/EmployeeFormPage";
import ImportPage from "./pages/ImportPage";
import OrgChartPage from "./pages/OrgChartPage";
import DirectoryPage from "./pages/DirectoryPage";
import SettingsPage from "./pages/SettingsPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SuperAdminDashboardPage from "./pages/SuperAdminDashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import MarketingDashboardPage from "./pages/MarketingDashboardPage";
import EmailAdminPage from "./pages/EmailAdminPage";
import PerformancePageV2 from "./pages/PerformancePageV2";
import ObjectiveFormPage from "./pages/ObjectiveFormPage";
import EvaluationTemplatesPage from "./pages/EvaluationTemplatesPage";
import ContinuousFeedbackPage from "./pages/ContinuousFeedbackPage";
import EmployeeCampaignDetailPage from "./pages/EmployeeCampaignDetailPage";
import AuditPage from "./pages/AuditPage";
import SkillsPage from "./pages/SkillsPage";
import JobProfilesPage from "./pages/JobProfilesPage";
import SkillCampaignsPage from "./pages/SkillCampaignsPage";
import SkillCampaignDetailPage from "./pages/SkillCampaignDetailPage";
import EmployeeSkillsPage from "./pages/EmployeeSkillsPage";
import DevelopmentPlansPage from "./pages/DevelopmentPlansPage";
import SkillsDashboardPage from "./pages/SkillsDashboardPage";
import SkillsCockpitPage from "./pages/SkillsCockpitPage";
import ManagerSkillsDashboard from "./pages/ManagerSkillsDashboard";
import EmployeeMySkillsPage from "./pages/EmployeeMySkillsPage";
import EmployeeAccountsPage from "./pages/EmployeeAccountsPage";
import MyEvaluationsPage from "./pages/MyEvaluationsPage";
import SelfEvaluationPage from "./pages/SelfEvaluationPage";
import MySkillsPage from "./pages/MySkillsPage";
import ProfilePage from "./pages/ProfilePage";
import EmployeeDashboardPage from "./pages/EmployeeDashboardPage";
import ManagerDashboardPage from "./pages/ManagerDashboardPage";
import ManagerEvaluationsPage from "./pages/ManagerEvaluationsPage";
import ManagerEvaluationFormPage from "./pages/ManagerEvaluationFormPage";
import TeamSkillsPage from "./pages/TeamSkillsPage";
import ObjectivesPage from "./pages/ObjectivesPage";

// Billing Pages
import PricingPage from "./pages/PricingPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SuperAdminBillingPage from "./pages/SuperAdminBillingPage";
import AICreditsPurchasePage from "./pages/AICreditsPurchasePage";

// AI Pages
import AIInsightsPage from "./pages/AIInsightsPage";
import ExecutiveViewPage from "./pages/ExecutiveViewPage";

// Evaluation TDR Pages (Module conforme au cahier des charges)
import CompetencyReferencePage from "./pages/CompetencyReferencePage";
import CareerReferentialPage from "./pages/CareerReferentialPage";
import MyCareerProfilePage from "./pages/MyCareerProfilePage";
import CareerIntelligencePage from "./pages/CareerIntelligencePage";
import MobilityPortalPage from "./pages/MobilityPortalPage";
import SuccessionPlansPage from "./pages/SuccessionPlansPage";
import MyAbsencesPage from "./pages/MyAbsencesPage";
import AbsencesAdminPage from "./pages/AbsencesAdminPage";
import AbsencesCalendarPage from "./pages/AbsencesCalendarPage";
import AbsencesDashboardPage from "./pages/AbsencesDashboardPage";
import AbsencesAnalyticsPage from "./pages/AbsencesAnalyticsPage";
import PayrollRubricsPage from "./pages/PayrollRubricsPage";
import PayrollRunsPage from "./pages/PayrollRunsPage";
import PayrollContractsPage from "./pages/PayrollContractsPage";
import MyPayslipsPage from "./pages/MyPayslipsPage";
import EvaluationCampaignsTDRPage from "./pages/EvaluationCampaignsTDRPage";

// Performance Evaluation Pages (Nouveau module refonte)
import EvaluationCampaignsPage from "./pages/EvaluationCampaignsPage";
import ManagerEvaluationCampaignFormPage from "./pages/ManagerEvaluationCampaignFormPage";
import PerformanceReportPage from "./pages/PerformanceReportPage";
import MyEvaluationsEmployeePage from "./pages/MyEvaluationsEmployeePage";
import PerformanceCampaignDetailPage from "./pages/PerformanceCampaignDetailPage";
import RHValidationPage from "./pages/RHValidationPage";

// Documentation Pages
import UserGuidePage from "./pages/UserGuidePage";

// Public Page (accessible without auth)
import PublicEmployeePage from "./pages/PublicEmployeePage";
import PublicDepartmentPage from "./pages/PublicDepartmentPage";
import SharedDirectoryPage from "./pages/SharedDirectoryPage";

// Protected Route Component with Plan checking
const ProtectedRoute = ({ children, allowedRoles, requiresCompany = false, requiresPro = false }) => {
  const { user, loading } = useAuth();
  const location = window.location;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-webtech-blue"></div>
      </div>
    );
  }
  
  if (!user) {
    // Distinguish "logged out by inactivity" from "never logged in"
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      localStorage.removeItem('lastActivity');
      return <Navigate to="/login?expired=true" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Redirect to admin page if SuperAdmin without company tries to access company-specific pages
  if (requiresCompany && !user.companyId) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// PRO-only Route Component (checks plan via PlanContext inside the component)
const ProProtectedRoute = ({ children, allowedRoles, requiresCompany = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-webtech-blue"></div>
      </div>
    );
  }
  
  if (!user) {
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      localStorage.removeItem('lastActivity');
      return <Navigate to="/login?expired=true" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (requiresCompany && !user.companyId) {
    return <Navigate to="/admin" replace />;
  }
  
  // SuperAdmin bypasses plan check
  if (user.role === 'SuperAdmin') {
    return children;
  }
  
  // Plan check will happen inside PlanProvider which wraps this
  return children;
};

function App() {
  return (
    <AuthProvider>
      <PlanProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </PlanProvider>
    </AuthProvider>
  );
}

function AppRoutes() {
  // Track module navigation for SuperAdmin growth metrics
  useModuleTracking();
  return (
    <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPagePremium />} />
            <Route path="/demande-demo" element={<DemoRequestPublicPage />} />
            <Route path="/landing-legacy" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Navigate to="/#pricing" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/get-started" element={<OnboardingPage />} />
            <Route path="/public/employee/:employeeId" element={<PublicEmployeePage />} />
            <Route path="/public/department/:departmentId" element={<PublicDepartmentPage />} />
            <Route path="/shared/directory/:shareToken" element={<SharedDirectoryPage />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <ExecutiveDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard-legacy" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/hr-indicators" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <HRIndicatorsPage />
            </ProtectedRoute>
          } />
          <Route path="/prospects" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <ProspectsAdminPage />
            </ProtectedRoute>
          } />
          <Route path="/my-dashboard" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager"]} requiresCompany={true}>
              <EmployeeDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/manager-dashboard" element={
            <ProtectedRoute allowedRoles={["Manager", "Admin", "SuperAdmin"]} requiresCompany={true}>
              <ManagerDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/evaluations" element={
            <ProtectedRoute allowedRoles={["Manager", "Admin", "SuperAdmin"]} requiresCompany={true} requiresPro={true}>
              <ManagerEvaluationsPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/team-skills" element={
            <ProtectedRoute allowedRoles={["Manager", "Admin", "SuperAdmin"]} requiresCompany={true}>
              <TeamSkillsPage />
            </ProtectedRoute>
          } />
          <Route path="/employees" element={
            <ProtectedRoute requiresCompany={true}>
              <EmployeesPage />
            </ProtectedRoute>
          } />
          <Route path="/employees/new" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <EmployeeFormPage />
            </ProtectedRoute>
          } />
          <Route path="/employees/:id/edit" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <EmployeeFormPage />
            </ProtectedRoute>
          } />
          <Route path="/import" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <ImportPage />
            </ProtectedRoute>
          } />
          <Route path="/orgchart" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <OrgChartPage />
            </ProtectedRoute>
          } />
          <Route path="/directory" element={
            <ProtectedRoute requiresCompany={true}>
              <DirectoryPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/executive-view" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <ExecutiveViewPage />
            </ProtectedRoute>
          } />
          <Route path="/audit" element={
            <ProtectedRoute allowedRoles={["Admin"]} requiresCompany={true}>
              <AuditPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <SuperAdminDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/legacy" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <SuperAdminPage />
            </ProtectedRoute>
          } />
          <Route path="/marketing" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <MarketingDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/emails" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <EmailAdminPage />
            </ProtectedRoute>
          } />
          
          {/* Objectives Route */}
          <Route path="/objectives" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true} requiresPro={true}>
              <ObjectivesPage />
            </ProtectedRoute>
          } />
          
          {/* Performance Module Routes */}
          <Route path="/performance" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true} requiresPro={true}>
              <PerformancePageV2 />
            </ProtectedRoute>
          } />
          <Route path="/performance/campaign/:campaignId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <PerformanceCampaignDetailPage />
            </ProtectedRoute>
          } />
          
          {/* Evaluation TDR Routes (Module conforme au cahier des charges) */}
          <Route path="/competency-reference" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <CompetencyReferencePage />
            </ProtectedRoute>
          } />

          {/* Career & Skills Intelligence — Phase 1 */}
          <Route path="/career/referential" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <CareerReferentialPage />
            </ProtectedRoute>
          } />
          <Route path="/career/profile" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <MyCareerProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/career/profile/:employeeId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <MyCareerProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/career/intelligence" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <CareerIntelligencePage />
            </ProtectedRoute>
          } />
          <Route path="/career/mobility" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <MobilityPortalPage />
            </ProtectedRoute>
          } />
          <Route path="/career/succession" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <SuccessionPlansPage />
            </ProtectedRoute>
          } />

          {/* Absences & Leave Management */}
          <Route path="/absences/me" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <MyAbsencesPage />
            </ProtectedRoute>
          } />
          <Route path="/absences/admin" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <AbsencesAdminPage />
            </ProtectedRoute>
          } />
          <Route path="/absences/calendar" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <AbsencesCalendarPage />
            </ProtectedRoute>
          } />
          <Route path="/absences/dashboard" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <AbsencesDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/absences/analytics" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <AbsencesAnalyticsPage />
            </ProtectedRoute>
          } />

          {/* Payroll & Compensation */}
          <Route path="/payroll/rubrics" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <PayrollRubricsPage />
            </ProtectedRoute>
          } />
          <Route path="/payroll/runs" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <PayrollRunsPage />
            </ProtectedRoute>
          } />
          <Route path="/payroll/contracts" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <PayrollContractsPage />
            </ProtectedRoute>
          } />
          <Route path="/payroll/my" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]} requiresCompany={true}>
              <MyPayslipsPage />
            </ProtectedRoute>
          } />
          <Route path="/evaluation-tdr" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <EvaluationCampaignsTDRPage />
            </ProtectedRoute>
          } />
          
          {/* Nouveau module Performance Evaluation */}
          <Route path="/evaluation-campaigns" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <EvaluationCampaignsPage />
            </ProtectedRoute>
          } />
          <Route path="/performance-report" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <PerformanceReportPage />
            </ProtectedRoute>
          } />
          <Route path="/my-evaluations-employee" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager"]} requiresCompany={true}>
              <MyEvaluationsEmployeePage />
            </ProtectedRoute>
          } />
          <Route path="/my-evaluations-employee/:evaluationId" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager"]} requiresCompany={true}>
              <MyEvaluationsEmployeePage />
            </ProtectedRoute>
          } />
          <Route path="/performance/evaluate-campaign/:employeeId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <ManagerEvaluationCampaignFormPage />
            </ProtectedRoute>
          } />
          <Route path="/performance-eval/manager/evaluate/:evaluationId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <ManagerEvaluationCampaignFormPage />
            </ProtectedRoute>
          } />
          
          {/* RH Validation Page */}
          <Route path="/rh-validation/:evaluationId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <RHValidationPage />
            </ProtectedRoute>
          } />
          
          <Route path="/performance/employee/:employeeId/campaign/:campaignId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <EmployeeCampaignDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/performance/feedback" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <ContinuousFeedbackPage />
            </ProtectedRoute>
          } />
          <Route path="/performance/objective/new" element={
            <ProtectedRoute requiresCompany={true}>
              <ObjectiveFormPage />
            </ProtectedRoute>
          } />
          <Route path="/performance/objective/:id" element={
            <ProtectedRoute requiresCompany={true}>
              <ObjectiveFormPage />
            </ProtectedRoute>
          } />
          <Route path="/performance/evaluate/:employeeId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <ManagerEvaluationCampaignFormPage />
            </ProtectedRoute>
          } />
          <Route path="/performance/templates" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <EvaluationTemplatesPage />
            </ProtectedRoute>
          } />
          
          {/* Skills Intelligence Module Routes */}
          <Route path="/skills/cockpit" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <SkillsCockpitPage />
            </ProtectedRoute>
          } />
          <Route path="/skills" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <SkillsPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/profiles" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <JobProfilesPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/evaluations" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <SkillCampaignsPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/campaigns" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <SkillCampaignsPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/campaigns/:campaignId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <SkillCampaignDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/upskilling" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <DevelopmentPlansPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/dashboard" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <SkillsDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/development-plans" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <DevelopmentPlansPage />
            </ProtectedRoute>
          } />
          <Route path="/skills/employee/:employeeId" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} requiresCompany={true}>
              <EmployeeSkillsPage />
            </ProtectedRoute>
          } />
          
          {/* Employee Accounts Management (Admin) */}
          <Route path="/settings/employee-accounts" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <EmployeeAccountsPage />
            </ProtectedRoute>
          } />
          
          {/* Manager Skills Dashboard */}
          <Route path="/skills/team" element={
            <ProtectedRoute allowedRoles={["Manager"]} requiresCompany={true}>
              <ManagerSkillsDashboard />
            </ProtectedRoute>
          } />
          
          {/* Employee Space (for employees to access their data) */}
          <Route path="/my-skills" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager"]} requiresCompany={true}>
              <MySkillsPage />
            </ProtectedRoute>
          } />
          <Route path="/my-skills/intelligence" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager"]} requiresCompany={true}>
              <EmployeeMySkillsPage />
            </ProtectedRoute>
          } />
          <Route path="/my-evaluations" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager", "Admin", "SuperAdmin"]} requiresCompany={true}>
              <MyEvaluationsPage />
            </ProtectedRoute>
          } />
          <Route path="/my-evaluations/self/:objectiveId" element={
            <ProtectedRoute allowedRoles={["Employé", "Manager", "Admin", "SuperAdmin"]} requiresCompany={true}>
              <SelfEvaluationPage />
            </ProtectedRoute>
          } />
          
          {/* Billing Routes */}
          <Route path="/billing/pricing" element={
            <ProtectedRoute>
              <PricingPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/checkout" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]}>
              <CheckoutPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/payment-return" element={
            <ProtectedRoute>
              <PaymentReturnPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/payment-cancelled" element={
            <ProtectedRoute>
              <PaymentReturnPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/subscription" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <SubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/credits" element={
            <ProtectedRoute allowedRoles={["Admin"]} requiresCompany={true}>
              <AICreditsPurchasePage />
            </ProtectedRoute>
          } />
          <Route path="/billing/credits/success" element={
            <ProtectedRoute allowedRoles={["Admin"]} requiresCompany={true}>
              <AICreditsPurchasePage />
            </ProtectedRoute>
          } />
          <Route path="/billing/credits/cancel" element={
            <ProtectedRoute allowedRoles={["Admin"]} requiresCompany={true}>
              <AICreditsPurchasePage />
            </ProtectedRoute>
          } />
          <Route path="/superadmin/billing" element={
            <ProtectedRoute allowedRoles={["SuperAdmin"]}>
              <SuperAdminBillingPage />
            </ProtectedRoute>
          } />
          
          {/* AI Insights Route */}
          <Route path="/ai-insights" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin"]} requiresCompany={true}>
              <AIInsightsPage />
            </ProtectedRoute>
          } />
          
          {/* Documentation Route */}
          <Route path="/user-guide" element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager", "Employé"]}>
              <UserGuidePage />
            </ProtectedRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

export default App;
